import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import RfPageFrame from '../layout/RfPageFrame';

export default function SolutionsDeepStrike() {
  return (
    <>
      <div className="rf-hero-band">
        <RfPageFrame className="!py-10 md:!py-14">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-[#5c6d7e] mb-2">Solutions · Human Trust · DeepStrike</p>
          <h1 className="text-2xl md:text-[2.25rem] font-semibold text-[#2c2c2c] tracking-tight max-w-3xl leading-tight">
            DeepStrike — Managed AI attack simulation campaigns
          </h1>
          <p className="mt-6 text-[1.05rem] text-[#5c5c5c] max-w-2xl leading-relaxed">
            Quarterly multi-vector exercises combining prompt injection emails, deepfake vishing, and synthetic identity probes. Fully managed — we design, deploy, measure, and remediate. White-labelled reporting for your board and insurers.
          </p>
          <span className="inline-block mt-4 text-xs px-3 py-1.5 rounded bg-[#f0faf5] text-[#0F6E56] font-medium">Recurring · Annual subscription</span>
        </RfPageFrame>
      </div>

      <section className="bg-white border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c] mb-8">Five attack channels</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: 'Email — Prompt Injection',
                desc: 'Emails with embedded prompt injection payloads that target copilot scanning workflows — testing whether employees notice their AI assistant behaving anomalously after processing the message.',
              },
              {
                title: 'AI-Cloned Voice (Vishing)',
                desc: 'Inbound calls from AI-cloned executive voices requesting urgent actions: wire transfers, credential resets, or sensitive data disclosure. Tests out-of-band verification reflexes.',
              },
              {
                title: 'Deepfake Video Conference',
                desc: 'Video meeting infiltration using synthetic faces and voices of known colleagues or executives — testing whether employees verify identity before acting on instructions.',
              },
              {
                title: 'AI-Personalised SMS / WhatsApp',
                desc: 'Lures built using publicly available information about the target, making them highly contextual and difficult to distinguish from legitimate messages.',
              },
              {
                title: 'Synthetic Identity Attacks',
                desc: 'Fake candidates, vendors, and partners with AI-generated profiles — testing procurement, HR, and onboarding verification controls against convincing synthetic identities.',
              },
            ].map(({ title, desc }) => (
              <div key={title} className="rf-card p-6 bg-white space-y-3">
                <h3 className="font-semibold text-[#2c2c2c]">{title}</h3>
                <p className="text-sm text-[#5c5c5c] leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </RfPageFrame>
      </section>

      <section className="bg-[#f0f7ff] border-b border-[#e5eef6]">
        <RfPageFrame className="py-16">
          <h2 className="text-xl md:text-2xl font-semibold text-[#2c2c2c] mb-8">What's included</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rf-card p-6 md:p-8 bg-white space-y-4">
              <h3 className="font-semibold text-[#2c2c2c]">Reporting & analytics</h3>
              <ul className="text-sm text-[#5c5c5c] leading-relaxed space-y-2">
                <li className="flex gap-2"><span className="text-[#005a9c] shrink-0">•</span>Per-user risk scoring with trend over time</li>
                <li className="flex gap-2"><span className="text-[#005a9c] shrink-0">•</span>Department heat maps to identify high-risk teams</li>
                <li className="flex gap-2"><span className="text-[#005a9c] shrink-0">•</span>Behavioural trend analysis across quarterly campaigns</li>
                <li className="flex gap-2"><span className="text-[#005a9c] shrink-0">•</span>White-labelled board and insurer reports</li>
                <li className="flex gap-2"><span className="text-[#005a9c] shrink-0">•</span>Compliance evidence for NIS2, DORA, EU AI Act, ISO 42001</li>
              </ul>
            </div>
            <div className="rf-card p-6 md:p-8 bg-white space-y-4">
              <h3 className="font-semibold text-[#2c2c2c]">Fully managed service</h3>
              <ul className="text-sm text-[#5c5c5c] leading-relaxed space-y-2">
                <li className="flex gap-2"><span className="text-[#005a9c] shrink-0">•</span>Scenario design and customisation per organisation</li>
                <li className="flex gap-2"><span className="text-[#005a9c] shrink-0">•</span>Campaign deployment and scheduling</li>
                <li className="flex gap-2"><span className="text-[#005a9c] shrink-0">•</span>Automated remediation workflows post-campaign</li>
                <li className="flex gap-2"><span className="text-[#005a9c] shrink-0">•</span>Targeted follow-up training for at-risk users</li>
                <li className="flex gap-2"><span className="text-[#005a9c] shrink-0">•</span>Quarterly review sessions with your security team</li>
              </ul>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-6">
            <Link to="/solutions/trustscore" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c] hover:text-[#004a82]">
              TrustScore — baseline assessment <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/solutions/copilotready" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-medium text-[#005a9c] hover:text-[#004a82]">
              CopilotReady — copilot readiness <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <p className="mt-10 text-[0.9375rem] text-[#5c5c5c]">
            <Link to="/solutions/security-for-ai" className="text-[#005a9c] font-medium hover:text-[#004a82]">
              ← Back to Security for AI
            </Link>
          </p>
        </RfPageFrame>
      </section>
    </>
  );
}
